import { db, type LiftXDatabase } from '../db';
import { createSettingsRepository, SETTING_KEYS, type SettingsRepository } from '../repositories/settingsRepository';
import { createPersonalRecordsRepository } from '../repositories/personalRecordsRepository';
import { exerciseUserStateId, hasNonDefaultOverlayState } from '../repositories/exerciseRepository';
import { isLive, tombstoneById } from '../tombstone';
import { builtInMeasurementTypeId } from '../seed/measurementTypes';
import { loadCatalogue, mergeOntoExisting } from '../seed/seedCatalogue';
import { SYNC_TABLE_KEYS, PR_AFFECTING_TABLES, syncTable, type SyncTableKey } from './syncTables';
import { pushTables, pullTables, uploadMedia, downloadMedia, type PushRowResult } from './syncHttp';
import type { ExerciseRecord, ExerciseUserStateRecord, SessionRecord, UserMediaRecord } from '../types';

/**
 * The client sync engine — "LiftX Sync Blueprint" §4–§9. One push→pull cycle:
 *
 *  1. **Push**: every synced table's *entire current row set* (tombstones
 *     included) goes up, every cycle — "Option B" from the blueprint's
 *     approval message (§7 decision): no Dexie schema migration, no dirty
 *     tracking. The server's content-aware upsert (`syncDb.ts`) makes
 *     re-sending unchanged rows a true no-op, so this is safe and cheap at
 *     personal scale rather than wasteful.
 *  2. **Purge**: a local tombstone whose push came back confirmed
 *     (`inserted`/`updated`/`unchanged` — never `forbidden`/`rejected`) is
 *     now durably on the server, so it is safe to hard-delete from Dexie.
 *     This is the other half of `../tombstone.ts`'s contract.
 *  3. **Pull**: incremental, from the last saved cursor (`settingsRepository`,
 *     `SETTING_KEYS.syncCursor`), repeated while any table reports
 *     `hasMore`. Every returned row is written with `bulkPut`. A pulled
 *     `userMedia` row is special-cased (`applyPulledUserMedia`) — see below.
 *  4. **Media upload**: every local `userMedia` row with a blob not yet
 *     confirmed uploaded (`uploadStatus !== 'uploaded'`, which also covers
 *     every row created before this field existed — Phase 6, no backfill
 *     needed) is uploaded via the existing `/api/sync/media/:id` endpoint.
 *     Runs after push+pull, since the endpoint requires the metadata row to
 *     already exist server-side. A single item's failure never fails the
 *     cycle — it just retries next time.
 *  5. **PR rebuild**: `personalRecords` is never synced directly (a
 *     rebuildable cache, blueprint §22) — if the pull touched any table that
 *     could change one, `personalRecordsRepository.rebuildAll()` runs once
 *     at the end, not per row.
 *
 * `userMedia` pulls never carry blob bytes on the wire (only metadata —
 * caption, ordering, `uploadStatus`; see `functions/api/_lib/syncTables.ts`).
 * `applyPulledUserMedia` covers the three cases that fall out of that: a
 * device that already has the blob keeps it and only merges metadata; a
 * device that doesn't, and the upload is confirmed, downloads it eagerly, in
 * the same cycle; a device that doesn't, and the upload isn't confirmed yet,
 * writes nothing for that id — it reappears in a later pull once the
 * originating device's upload lands and `updated_at` advances again.
 * `thumbnailBlob` is not synced at all: the media endpoint carries one blob
 * per id, and extending it is out of this phase's scope — a downloaded item
 * always has `thumbnailBlob: null`. `MediaGallery.tsx`'s existing fallback
 * (`item.thumbnailBlob ?? (kind === 'image' ? item.blob : null)`) already
 * handles that gracefully for images (shows the main blob instead); a
 * downloaded video with no thumbnail shows the gallery's generic
 * placeholder icon rather than a real preview frame, a real but accepted
 * limitation of not touching the server endpoint this phase.
 */

export type SyncStatus = 'idle' | 'syncing' | 'synced' | 'error';

export interface SyncCycleResult {
  status: 'synced' | 'error';
  reason?: string;
}

type Listener = (status: SyncStatus) => void;

let currentStatus: SyncStatus = 'idle';
const listeners = new Set<Listener>();

function setStatus(status: SyncStatus): void {
  currentStatus = status;
  for (const listener of listeners) listener(status);
}

export function getSyncStatus(): SyncStatus {
  return currentStatus;
}

/** Returns an unsubscribe function. Fires once immediately with the current status. */
export function subscribeSyncStatus(listener: Listener): () => void {
  listeners.add(listener);
  listener(currentStatus);
  return () => listeners.delete(listener);
}

/**
 * `sessions.activeRestTimer` is deliberately excluded from the wire —
 * blueprint §3, `functions/api/_lib/syncTables.ts`'s own comment: live
 * in-progress UI state that must never resurrect on a second device or be
 * wiped by one. Stripped before push (the server would just ignore it
 * anyway); merged back onto every pulled `sessions` row from whatever is
 * already on this device before it's written.
 */
function forPush(tableKey: SyncTableKey, row: Record<string, unknown>): Record<string, unknown> {
  if (tableKey !== 'sessions') return row;
  const { activeRestTimer: _activeRestTimer, ...rest } = row;
  return rest;
}

/**
 * The active `exercises` sync surface is `source: 'custom'` rows only —
 * "Multi-user identity architecture" review, round 2, §4/§16. A catalogue
 * exercise's content is bundled with the app and seeded locally on every
 * device already; it has no reason to ever leave this device via push.
 * Legacy `source: 'exercisedb'` rows already sitting in this local table
 * (or, historically, in `liftx-sync` from before this change) are simply
 * never included here going forward — not deleted, not migrated in this
 * phase (see the review's §16 for why cleanup is a separate, later step).
 */
function isPushableExercise(row: Record<string, unknown>): boolean {
  return row.source !== 'exercisedb';
}

async function readAllRows(
  database: LiftXDatabase,
  tableKey: SyncTableKey,
): Promise<Record<string, unknown>[]> {
  const rows = await syncTable(database, tableKey).toArray();
  const pushable = tableKey === 'exercises' ? rows.filter(isPushableExercise) : rows;
  return pushable.map((row) => forPush(tableKey, row as unknown as Record<string, unknown>));
}

/**
 * Keeps the local `exerciseUserState` table in step with the corresponding
 * `exercises` rows' own overlay fields — the same "scan local state and
 * reconcile" shape `reconcileMeasurementTypeDuplicates` already uses below,
 * just building rows up rather than tombstoning duplicates down. Runs once
 * per cycle, before push, so a favourite/note/override made since the last
 * cycle is reflected in *this* cycle's push.
 *
 * A catalogue exercise that has returned to fully-default state gets its
 * existing overlay row tombstoned, not silently dropped from future
 * pushes — the correction has to actually reach the server and every other
 * device, or an old "still favourited" value would stay stuck there
 * forever (the same failure mode `hasNonDefaultOverlayState`-gated
 * "only push if currently non-default" would have, without this).
 */
export async function reconcileExerciseUserState(database: LiftXDatabase): Promise<void> {
  const catalogueExercises = await database.exercises.where('source').equals('exercisedb').toArray();
  const now = Date.now();

  for (const exercise of catalogueExercises) {
    const stateId = exerciseUserStateId(exercise.id);
    const existing = await database.exerciseUserState.get(stateId);
    const touched = hasNonDefaultOverlayState(exercise);

    if (!touched) {
      if (existing !== undefined && isLive(existing)) {
        await tombstoneById(database.exerciseUserState, stateId, now);
      }
      continue;
    }

    const overrideActive = exercise.hasManualOverride;
    const desired: ExerciseUserStateRecord = {
      id: stateId,
      catalogueExerciseId: exercise.id,
      isFavourite: exercise.isFavourite,
      isArchived: exercise.isArchived,
      notes: exercise.notes,
      tags: exercise.tags,
      hasManualOverride: exercise.hasManualOverride,
      defaultRestSeconds: exercise.defaultRestSeconds,
      defaultIncrementG: exercise.defaultIncrementG,
      mediaIds: exercise.mediaIds,
      thumbnailMediaId: exercise.thumbnailMediaId,
      overrideName: overrideActive ? exercise.name : null,
      overrideNameSearch: overrideActive ? exercise.nameSearch : null,
      overrideDescription: overrideActive ? exercise.description : null,
      overrideInstructions: overrideActive ? exercise.instructions : null,
      overrideTips: overrideActive ? exercise.tips : null,
      overrideCategory: overrideActive ? exercise.category : null,
      overrideDifficulty: overrideActive ? exercise.difficulty : null,
      overrideModality: overrideActive ? exercise.modality : null,
      overrideLoadType: overrideActive ? exercise.loadType : null,
      overrideBodyParts: overrideActive ? exercise.bodyParts : null,
      overrideTargetMuscles: overrideActive ? exercise.targetMuscles : null,
      overrideSecondaryMuscles: overrideActive ? exercise.secondaryMuscles : null,
      overrideEquipment: overrideActive ? exercise.equipment : null,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
      deletedAt: null,
    };

    // Avoid bumping `updatedAt` (and so triggering a real, if cheap, push)
    // on a cycle where nothing about the overlay actually changed.
    const unchanged =
      existing !== undefined &&
      isLive(existing) &&
      JSON.stringify({ ...existing, updatedAt: 0, createdAt: 0 }) === JSON.stringify({ ...desired, updatedAt: 0, createdAt: 0 });
    if (unchanged) continue;

    await database.exerciseUserState.put(desired);
  }
}

const PUSH_CHUNK_SIZE = 200;

/**
 * Pushes every table's full current state, chunked so no single table
 * exceeds the server's per-request row cap.
 *
 * `settings` supplies the device's own last-saved *pull* cursor, forwarded
 * on every push request unchanged — this is what lets the server tell a
 * stale push (from a device that hasn't caught up to a remote delete yet)
 * apart from a deliberate one, without the client tracking anything new;
 * the cursor already exists for pull's own purposes (`syncDb.ts`'s
 * `upsertRow`/`hasObserved` is where the server-side half of this lives).
 */
async function pushAll(
  database: LiftXDatabase,
  settings: SettingsRepository,
  fetchImpl: typeof fetch,
): Promise<{ ok: true; resultsByTable: Map<SyncTableKey, PushRowResult[]> } | { ok: false; reason: string }> {
  const allRowsByTable = new Map<SyncTableKey, Record<string, unknown>[]>();
  for (const key of SYNC_TABLE_KEYS) allRowsByTable.set(key, await readAllRows(database, key));

  const cursor = await settings.get<string | null>(SETTING_KEYS.syncCursor, null);

  const maxRows = Math.max(0, ...[...allRowsByTable.values()].map((rows) => rows.length));
  const rounds = Math.max(1, Math.ceil(maxRows / PUSH_CHUNK_SIZE));
  const resultsByTable = new Map<SyncTableKey, PushRowResult[]>();

  for (let round = 0; round < rounds; round++) {
    const body: Partial<Record<SyncTableKey, Record<string, unknown>[]>> = {};
    for (const [key, rows] of allRowsByTable) {
      const chunk = rows.slice(round * PUSH_CHUNK_SIZE, (round + 1) * PUSH_CHUNK_SIZE);
      if (chunk.length > 0) body[key] = chunk;
    }
    if (Object.keys(body).length === 0) continue;

    const outcome = await pushTables(body, cursor, fetchImpl);
    if (outcome.outcome !== 'success') {
      return { ok: false, reason: outcome.outcome === 'server-error' ? `server-error ${String(outcome.status)}` : outcome.outcome };
    }
    for (const [key, rowResults] of Object.entries(outcome.results) as [SyncTableKey, PushRowResult[]][]) {
      const existing = resultsByTable.get(key) ?? [];
      resultsByTable.set(key, [...existing, ...rowResults]);
    }
  }

  return { ok: true, resultsByTable };
}

/** Hard-deletes local tombstones whose delete is now durably confirmed on the server. */
async function purgeConfirmedTombstones(
  database: LiftXDatabase,
  resultsByTable: Map<SyncTableKey, PushRowResult[]>,
): Promise<void> {
  for (const [key, rowResults] of resultsByTable) {
    const confirmedIds = new Set(
      rowResults.filter((r) => r.status === 'inserted' || r.status === 'updated' || r.status === 'unchanged').map((r) => r.id),
    );
    if (confirmedIds.size === 0) continue;

    const table = syncTable(database, key);
    const rows = await table.bulkGet([...confirmedIds]);
    const toDelete = rows.filter(
      (row): row is { id: string; deletedAt?: number | null } =>
        row !== undefined && (row as { deletedAt?: number | null }).deletedAt != null,
    );
    if (toDelete.length > 0) await table.bulkDelete(toDelete.map((row) => row.id));
  }
}

/**
 * `sessions` needs the local-only merge described above; every other table
 * writes the pulled row verbatim — it is already the complete, correct shape
 * for that table (extra bookkeeping fields like `updatedAt` on a Dexie type
 * that doesn't declare one are simply carried along, unindexed and unread).
 */
async function applyPulledRow(
  database: LiftXDatabase,
  tableKey: SyncTableKey,
  row: Record<string, unknown>,
  fetchImpl: typeof fetch,
): Promise<void> {
  if (tableKey === 'sessions') {
    const existing = await database.sessions.get(row.id as string);
    await database.sessions.put({ ...row, activeRestTimer: existing?.activeRestTimer ?? null } as unknown as SessionRecord);
    return;
  }

  if (tableKey === 'userMedia') {
    await applyPulledUserMedia(database, row, fetchImpl);
    return;
  }

  if (tableKey === 'exercises') {
    await applyPulledExercise(database, row);
    return;
  }

  if (tableKey === 'exerciseUserState') {
    await applyPulledExerciseUserState(database, row, fetchImpl);
    return;
  }

  const table = syncTable(database, tableKey);
  await table.put(row as unknown as { id: string });
}

/**
 * Every overlay-owned field `exerciseUserState` represents, reset to its
 * default value. Deliberately excludes anything catalogue-owned (name,
 * instructions, taxonomy, modality/loadType) — those are handled
 * separately in `resetExerciseOverlayToDefault`, only when an override was
 * actually active, since an unoverridden row's content was never touched
 * by the overlay in the first place.
 */
const OVERLAY_FIELD_DEFAULTS = {
  isFavourite: false,
  isArchived: false,
  notes: null,
  tags: [],
  hasManualOverride: false,
  defaultRestSeconds: null,
  defaultIncrementG: null,
  mediaIds: [],
  thumbnailMediaId: null,
} as const satisfies Partial<ExerciseRecord>;

/**
 * `description`/`tips`/`category`/`difficulty` are never populated by the
 * bundled catalogue dataset (`seedCatalogue.ts`'s `toRecord()`: "Absent
 * from this dataset") — they're user-owned content from the moment a
 * catalogue row exists, exactly like `exercise_user_state`'s own
 * `override*` columns for them. `mergeOntoExisting` deliberately never
 * refreshes these on an ordinary re-seed (they'd wipe a still-live
 * override), so restoring them to their catalogue default has to happen
 * here explicitly rather than by relying on `mergeOntoExisting` alone.
 */
const CATALOGUE_CONTENT_DEFAULTS = {
  description: null,
  tips: [],
  category: null,
  difficulty: null,
} as const satisfies Partial<ExerciseRecord>;

/**
 * Resets a catalogue exercise back to having no overlay at all — the
 * counterpart to a tombstoned `exerciseUserState` pull. Every overlay
 * field returns to default; if the row was overridden, its content also
 * returns to the bundled catalogue's own values (reusing `mergeOntoExisting`,
 * the exact same reset `customExerciseRepository.ts`'s `revertOverride()`
 * already performs for the equivalent local action — not a second
 * implementation of it) rather than being left at whatever the override had
 * set. An unoverridden row's content was never touched by the overlay, so
 * nothing beyond the overlay fields themselves needs resetting. Never
 * touches a `source: 'custom'` row — there is no catalogue entry to reset
 * it from, and a custom row is never eligible for an overlay to begin with.
 */
async function resetExerciseOverlayToDefault(
  database: LiftXDatabase,
  existing: ExerciseRecord,
  fetchImpl: typeof fetch,
): Promise<void> {
  if (existing.source !== 'exercisedb') return;

  if (!existing.hasManualOverride) {
    await database.exercises.put({ ...existing, ...OVERLAY_FIELD_DEFAULTS });
    return;
  }

  let contentRestored: ExerciseRecord = { ...existing, ...CATALOGUE_CONTENT_DEFAULTS };
  try {
    const catalogue = await loadCatalogue(fetchImpl);
    const entry = catalogue.find((candidate) => candidate.id === existing.catalogueRecordId);
    if (entry !== undefined) {
      contentRestored = { ...mergeOntoExisting(existing, entry, Date.now()), ...CATALOGUE_CONTENT_DEFAULTS };
    }
    // entry === undefined: the catalogue no longer has this exercise (rare)
    // — the taxonomy/instructions fields are left exactly as the override
    // set them rather than guessed at; the overlay fields below still reset
    // correctly either way.
  } catch {
    // Best-effort — a network hiccup fetching the bundled catalogue must
    // never fail the whole sync cycle (mirrors the accepted best-effort
    // pattern already used elsewhere in this file, e.g. media downloads).
    // Overlay fields still reset below; taxonomy content is left as the
    // override set it until a later cycle can retry.
  }

  await database.exercises.put({ ...contentRestored, ...OVERLAY_FIELD_DEFAULTS });
}

/**
 * A pulled catalogue-exercise overlay merges its fields onto the matching
 * local `exercises` row — that row is what the UI actually reads
 * (favourite badges, notes, etc.), so a pulled overlay change has to land
 * there to be visible, not only in the `exerciseUserState` mirror itself.
 * The mirror row is written verbatim too, so `reconcileExerciseUserState`'s
 * next pass sees this device already has it and doesn't re-derive an
 * identical row for no reason.
 *
 * A tombstoned overlay means "this account no longer has any overlay for
 * this exercise" — `resetExerciseOverlayToDefault` above puts the local
 * exercise back to having none, rather than leaving it at whatever the
 * last-known-live overlay state happened to be. Without this, a device
 * that received the *live* overlay before it was later un-favourited/
 * un-overridden elsewhere would keep the stale state forever, and its own
 * next `reconcileExerciseUserState` pass would read that stale state as
 * "still touched" and revive the very overlay row this pull just
 * tombstoned — a resurrection loop, not just cosmetic staleness.
 *
 * If this device hasn't seeded its own copy of the catalogue exercise yet
 * (shouldn't normally happen — seeding always runs before sync — but
 * defensively handled), there's nothing local to reset or overlay onto.
 */
async function applyPulledExerciseUserState(
  database: LiftXDatabase,
  row: Record<string, unknown>,
  fetchImpl: typeof fetch,
): Promise<void> {
  await database.exerciseUserState.put(row as unknown as ExerciseUserStateRecord);

  const catalogueExerciseId = row.catalogueExerciseId as string;
  const existing = await database.exercises.get(catalogueExerciseId);
  if (existing === undefined) return;

  if (row.deletedAt != null) {
    await resetExerciseOverlayToDefault(database, existing, fetchImpl);
    return;
  }

  const overrideActive = row.hasManualOverride === true;
  const updated: ExerciseRecord = {
    ...existing,
    isFavourite: row.isFavourite as boolean,
    isArchived: row.isArchived as boolean,
    notes: row.notes as string | null,
    tags: row.tags as string[],
    hasManualOverride: row.hasManualOverride as boolean,
    defaultRestSeconds: row.defaultRestSeconds as number | null,
    defaultIncrementG: row.defaultIncrementG as number | null,
    mediaIds: row.mediaIds as string[],
    thumbnailMediaId: row.thumbnailMediaId as string | null,
    ...(overrideActive
      ? {
          name: row.overrideName as string,
          nameSearch: row.overrideNameSearch as string,
          description: row.overrideDescription as string | null,
          instructions: row.overrideInstructions as string[],
          tips: row.overrideTips as string[],
          category: row.overrideCategory as string | null,
          difficulty: row.overrideDifficulty as string | null,
          modality: row.overrideModality as ExerciseRecord['modality'],
          loadType: row.overrideLoadType as ExerciseRecord['loadType'],
          bodyParts: row.overrideBodyParts as string[],
          targetMuscles: row.overrideTargetMuscles as string[],
          secondaryMuscles: row.overrideSecondaryMuscles as string[],
          equipment: row.overrideEquipment as string[],
        }
      : {}),
  };
  await database.exercises.put(updated);
}

/**
 * A pulled catalogue exercise (`source: 'exercisedb'`) never overwrites its
 * own local content — only the user-owned overlay fields land. Defense in
 * depth alongside the server-side narrowing in `upsertRows`'s
 * `isCatalogueOverlayRow` (`functions/api/_lib/syncDb.ts`): that fix alone
 * is sufficient once every device is on it, but this also protects against
 * a row pushed by an old, not-yet-updated client in the meantime. Content
 * fields mirror exactly what `mergeOntoExisting` (`../seed/seedCatalogue.ts`)
 * already treats as catalogue-owned — reused, not redefined. An overridden
 * row (`hasManualOverride: true`, on either side of this pull) is genuinely
 * user-authored content by that point, so it's applied in full, same as any
 * other table.
 */
async function applyPulledExercise(database: LiftXDatabase, row: Record<string, unknown>): Promise<void> {
  if (row.source !== 'exercisedb') {
    await database.exercises.put(row as unknown as ExerciseRecord);
    return;
  }

  const existing = await database.exercises.get(row.id as string);
  if (existing === undefined || existing.hasManualOverride || row.hasManualOverride === true) {
    await database.exercises.put(row as unknown as ExerciseRecord);
    return;
  }

  const {
    name: _name,
    nameSearch: _nameSearch,
    instructions: _instructions,
    modality: _modality,
    loadType: _loadType,
    bodyParts: _bodyParts,
    targetMuscles: _targetMuscles,
    secondaryMuscles: _secondaryMuscles,
    equipment: _equipment,
    catalogueRecordId: _catalogueRecordId,
    ...overlay
  } = row;
  await database.exercises.put({ ...existing, ...overlay } as ExerciseRecord);
}

/**
 * `userMedia`'s three cases — see this file's own header comment for the
 * full reasoning. `existing !== undefined` covers both "this device created
 * it" and "this device downloaded it on an earlier cycle"; either way, the
 * blob it already has is authoritative and never overwritten by a pull.
 */
async function applyPulledUserMedia(
  database: LiftXDatabase,
  row: Record<string, unknown>,
  fetchImpl: typeof fetch,
): Promise<void> {
  const id = row.id as string;
  const existing = await database.userMedia.get(id);

  if (existing !== undefined) {
    await database.userMedia.put({
      ...existing,
      ...row,
      blob: existing.blob,
      thumbnailBlob: existing.thumbnailBlob,
    } as unknown as UserMediaRecord);
    return;
  }

  if (row.uploadStatus !== 'uploaded') return; // not downloadable yet — see header comment

  const outcome = await downloadMedia(id, fetchImpl);
  if (outcome.outcome !== 'success') return; // best-effort — this id is retried on a later pull

  await database.userMedia.put({
    ...row,
    blob: outcome.blob,
    thumbnailBlob: null, // never synced — see header comment
  } as unknown as UserMediaRecord);
}

/**
 * Every local `userMedia` row with a blob not yet confirmed uploaded — a
 * whole-table scan each cycle, matching push's own no-dirty-tracking
 * philosophy, not a queue. `deletedAt`-tombstoned rows are skipped: there is
 * no point spending an upload on a blob that's already been deleted, even
 * though the tombstone row itself still physically exists locally until
 * `purgeConfirmedTombstones` catches up.
 */
async function uploadPendingMedia(database: LiftXDatabase, fetchImpl: typeof fetch): Promise<void> {
  const rows = await database.userMedia.toArray();

  for (const row of rows) {
    if (!isLive(row) || row.uploadStatus === 'uploaded') continue;

    try {
      const outcome = await uploadMedia(row.id, row.blob, row.mimeType, fetchImpl);
      if (outcome.outcome === 'success') {
        await database.userMedia.update(row.id, { uploadStatus: 'uploaded' });
      }
      // Any other outcome — not-found, network/server error — is left as
      // 'pending' and retried next cycle, without failing this one.
    } catch {
      // A single item's upload must never abort the rest of the batch.
    }
  }
}

/**
 * Closes the exact gap that let a rename-based cleanup of `measurementTypes`
 * duplicates come back: a stale device that still holds a pre-fix, non-
 * canonical id for a built-in slug re-pushes it every cycle regardless
 * (Option B pushes each table's entire current state unconditionally — see
 * this file's header comment), and a plain server-side primary-key rename
 * leaves no tombstone behind for the old id, so the server treats that push
 * as a brand-new, unprotected row. Any *other* device that later pulls it
 * would otherwise write it straight in as a second live row for a slug it
 * already has a canonical entry for.
 *
 * Runs once after a pull cycle that touched `measurementTypes` at all — not
 * only when the canonical row itself was one of the pulled rows, since an
 * already-synced device's own next canonical pull may be long past (nothing
 * about the canonical changed), while the stale duplicate keeps arriving.
 * Re-scanning local state on every such pull, rather than only reacting to
 * the specific incoming row, is what makes this catch the duplicate no
 * matter which of the two rows (canonical or stale) was the one that just
 * came in.
 *
 * Only ever tombstones — via the same `tombstoneById` convention every other
 * delete in this codebase uses, so the resolution still syncs back out on
 * the next push — and only when a *live* canonical `built_in_<slug>` row is
 * already confirmed present locally for that slug, so a slug can never end
 * up with zero live rows because of this pass (no data loss if the canonical
 * hasn't arrived yet). The canonical row is never itself a candidate, and
 * custom types (`isBuiltIn: false`) are never inspected at all. Idempotent:
 * a duplicate that's already tombstoned is excluded by `isLive` and left
 * completely alone on every subsequent run.
 */
async function reconcileMeasurementTypeDuplicates(database: LiftXDatabase): Promise<void> {
  const rows = await database.measurementTypes.filter(isLive).toArray();
  const liveCanonicalSlugs = new Set(
    rows.filter((row) => row.isBuiltIn && row.id === builtInMeasurementTypeId(row.slug)).map((row) => row.slug),
  );
  if (liveCanonicalSlugs.size === 0) return;

  const now = Date.now();
  for (const row of rows) {
    if (!row.isBuiltIn) continue;
    if (row.id === builtInMeasurementTypeId(row.slug)) continue;
    if (!liveCanonicalSlugs.has(row.slug)) continue;
    await tombstoneById(database.measurementTypes, row.id, now);
  }
}

async function pullAll(
  database: LiftXDatabase,
  settings: SettingsRepository,
  fetchImpl: typeof fetch,
): Promise<{ ok: true; touchedPrTables: boolean } | { ok: false; reason: string }> {
  let cursor = await settings.get<string | null>(SETTING_KEYS.syncCursor, null);
  let touchedPrTables = false;
  let touchedMeasurementTypes = false;

  for (;;) {
    const outcome = await pullTables(cursor, fetchImpl);
    if (outcome.outcome !== 'success') {
      return { ok: false, reason: outcome.outcome === 'server-error' ? `server-error ${String(outcome.status)}` : outcome.outcome };
    }

    for (const [key, table] of Object.entries(outcome.tables) as [SyncTableKey, { rows: Record<string, unknown>[] }][]) {
      if (table.rows.length === 0) continue;
      for (const row of table.rows) await applyPulledRow(database, key, row, fetchImpl);
      if (PR_AFFECTING_TABLES.includes(key)) touchedPrTables = true;
      if (key === 'measurementTypes') touchedMeasurementTypes = true;
    }

    cursor = outcome.cursor;
    await settings.set(SETTING_KEYS.syncCursor, cursor);

    if (!outcome.hasMore) break;
  }

  if (touchedMeasurementTypes) await reconcileMeasurementTypeDuplicates(database);

  return { ok: true, touchedPrTables };
}

let activeCycle: Promise<SyncCycleResult> | null = null;

/**
 * Runs one full push→purge→pull→PR-rebuild cycle. Concurrency-guarded: a
 * call while a cycle is already running returns the SAME in-flight promise
 * rather than starting a second, overlapping one (blueprint §9's
 * "at most one sync cycle in flight" rule) — the caller doesn't need to know
 * whether it triggered the run or piggy-backed on an existing one.
 *
 * Never throws. A network or server failure is reported via the returned
 * result and the `'error'` status, not an exception — the whole design
 * (content-aware upsert, resumable cursor) makes "just try again next tick"
 * a fully adequate retry strategy, so there is no separate backoff state to
 * manage here.
 */
export function runSyncCycle(database: LiftXDatabase = db, fetchImpl: typeof fetch = fetch): Promise<SyncCycleResult> {
  if (activeCycle !== null) return activeCycle;

  const run = async (): Promise<SyncCycleResult> => {
    const settings = createSettingsRepository(database);

    // Local-data-ownership safety gate — Phase G fix design ("cross-account
    // local-storage race"). The final backstop: refuses to push or pull
    // unless the local tables are CONFIRMED settled for the exact account
    // the server most recently confirmed authenticated. Deliberately
    // independent of whatever gated this call — the scheduler's own
    // immediate/interval/online/visibility triggers, `SyncCard`'s manual
    // "Sync now" button, or any future caller all funnel through this one
    // function, so "authenticated" alone is never, by itself, sufficient
    // here. `resetState !== 'idle'` catches a reset that is still running or
    // that failed last time; `owner === null` catches a device that has
    // never had ownership established; comparing `owner.email` against
    // `lastVerified.email` (both written together, in that order, only once
    // `AuthProvider.tsx`'s `revalidate()` has already awaited any needed
    // reset to completion) is what actually ties this check back to "the
    // account currently authenticated," without this module importing
    // anything from the auth layer — see `resetLocalDatabase.ts`'s own doc
    // for why that layering is deliberate.
    const [resetState, owner, lastVerified] = await Promise.all([
      settings.getLocalResetState(),
      settings.getLocalDataOwner(),
      settings.getLastVerified(),
    ]);
    const settled =
      resetState === 'idle' && owner !== null && lastVerified !== null && owner.email === lastVerified.email;
    if (!settled) {
      setStatus('error');
      return { status: 'error', reason: 'local-data-not-settled' };
    }

    setStatus('syncing');

    // Before push, so a favourite/note/override made since the last cycle
    // is reflected in this cycle's `exerciseUserState` push, not one cycle
    // late.
    await reconcileExerciseUserState(database);

    const pushResult = await pushAll(database, settings, fetchImpl);
    if (!pushResult.ok) {
      setStatus('error');
      return { status: 'error', reason: pushResult.reason };
    }
    await purgeConfirmedTombstones(database, pushResult.resultsByTable);

    const pullResult = await pullAll(database, settings, fetchImpl);
    if (!pullResult.ok) {
      setStatus('error');
      return { status: 'error', reason: pullResult.reason };
    }

    // Runs after push+pull — the media endpoint requires the metadata row
    // to already exist server-side. Never fails the cycle: a media transfer
    // problem is a soft, best-effort concern layered on top of the
    // authoritative metadata sync above, not part of its success contract.
    await uploadPendingMedia(database, fetchImpl);

    if (pullResult.touchedPrTables) {
      await createPersonalRecordsRepository(database).rebuildAll();
    }

    // Display-only — nothing reads this to make a sync decision (that's the
    // cursor's job). Written only on a genuine success, never on 'error', so
    // a failed cycle doesn't masquerade as a recent sync in Settings.
    await settings.set(SETTING_KEYS.lastSyncedAt, Date.now());

    setStatus('synced');
    return { status: 'synced' };
  };

  activeCycle = run().finally(() => {
    activeCycle = null;
  });
  return activeCycle;
}
